<template>
    <main class="min-h-screen bg-background  overflow-hidden">
        <UnderwaterBg class="fixed inset-0 z-0" />
        
        <!-- Animated Ducks -->
        <div class="fixed inset-0 z-0 pointer-events-none">
            <img src="/ducksImages/duckIcon.webp" class="swimming-duck duck1 w-16 h-16 opacity-20" />
            <img src="/ducksImages/duckIcon.webp" class="swimming-duck duck2 w-20 h-20 opacity-20" />
        </div>

        <div class="relative z-10 flex min-h-screen items-center justify-center p-4">
            <div class="w-full max-w-md space-y-8 rounded-2xl bg-white/80 backdrop-blur-sm p-8 shadow-xl text-center">
                <div class="relative">
                    <div class="absolute inset-0 flex items-center justify-center">
                        <div class="animate-ping absolute inline-flex h-16 w-16 rounded-full bg-primary opacity-20"></div>
                    </div>
                    <v-icon size="64" color="primary" class="relative">mdi-email-check</v-icon>
                </div>

                <div class="space-y-4">
                    <h2 class="text-2xl font-bold text-gray-900">Ahoy, Captain! 🚢</h2>
                    <p class="text-gray-600">
                        We've dispatched a message in a bottle to
                        <span class="font-semibold text-primary">{{ email }}</span>
                    </p>
                    <p class="text-sm text-gray-500">
                        Please check your inbox and click the confirmation link to begin your duck-finding adventure!
                    </p>
                </div>

                <div class="space-y-4 pt-4">
                    <v-btn
                        color="primary"
                        :loading="resending"
                        block
                        size="large"
                        class="rounded-lg"
                        @click="resendConfirmation"
                    >
                        {{ resending ? 'Sending...' : 'Resend Confirmation Email' }}
                    </v-btn>

                    <v-btn
                        variant="text"
                        block
                        @click="router.push({ path: '/', query: { showLogin: 'true' } });"
                        class="mt-4"
                    >
                        Return to Port (Login)
                    </v-btn>
                </div>
            </div>
        </div>

        <v-snackbar
            v-model="showNotification"
            :color="notificationColor"
            :timeout="3000"
            location="top"
            class="text-center"
        >
            {{ notificationMessage }}
        </v-snackbar>
    </main>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';
import { Constant } from '@/config/constants';
import { UnderwaterBg } from '@/components/ui/underwaterBg';

const route = useRoute();
const router = useRouter();
const email = route.params.email as string;

const resending = ref(false);
const showNotification = ref(false);
const notificationMessage = ref('');
const notificationColor = ref('');

const showMessage = (message: string, color: 'success' | 'error') => {
    notificationMessage.value = message;
    notificationColor.value = color;
    showNotification.value = true;
};

const resendConfirmation = async () => {
    try {
        resending.value = true;
        await axios.post(Constant.API_URL + Constant.RESEND_CONFIRMATION, { email });
        showMessage('Confirmation email resent successfully!', 'success');
    } catch (error: any) {
        const message = error.response?.status === 409 
            ? 'Email already verified. Please login.'
            : 'Failed to resend confirmation email.';
        showMessage(message, 'error');
        
        if (error.response?.status === 409) {
            setTimeout(() => router.push({ path: '/', query: { showLogin: 'true' } }), 2000);
        }
    } finally {
        resending.value = false;
    }
};
</script>

<style scoped>
.swimming-duck {
    position: absolute;
    animation: swim 30s infinite linear;
}

.duck1 {
    top: 30%;
    animation-delay: 0s;
}

.duck2 {
    top: 60%;
    animation-delay: -15s;
}

@keyframes swim {
    0% {
        transform: translateX(-100px) rotate(0deg);
    }
    100% {
        transform: translateX(calc(100vw + 100px)) rotate(360deg);
    }
}
</style>
